import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
} from "@/components/ui/sidebar";
import {
  Trophy, Target, Users, BarChart3, Settings, LogOut,
  Calendar, MessageCircle, FileText, Tv, Award
} from "lucide-react";
import { useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/hooks/useAuth";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export function AppSidebar() {
  const [location, setLocation] = useLocation();
  const { user } = useAuth();

  const isOrganizer = user?.role === 'organizer';

  // Circuitos que el usuario organiza (para mostrar acceso a administración)
  const { data: organizedCircuits = [] } = useQuery<{ id: string; name: string }[]>({
    queryKey: ['/api/my-organized-circuits', user?.id],
    queryFn: async () => {
      const res = await fetch('/api/circuits', { credentials: 'include' });
      if (!res.ok) return [];
      const circuitList: { id: string; name: string }[] = await res.json();
      const checks = await Promise.all(
        circuitList.map(async (c) => {
          const r = await fetch(`/api/circuits/${c.id}/organizers`, { credentials: 'include' });
          if (!r.ok) return null;
          const json = await r.json();
          return json.isOrganizer ? { id: c.id, name: c.name } : null;
        })
      );
      return checks.filter((c): c is { id: string; name: string } => c !== null);
    },
    enabled: !!user,
    staleTime: 60_000,
  });

  const menuItems = [
    {
      title: "Dashboard",
      url: "/dashboard",
      icon: BarChart3,
      visible: true
    },
    {
      title: "🔴 En Vivo",
      url: "/en-vivo",
      icon: Tv,
      visible: !isOrganizer
    },
    {
      title: "Torneos",
      url: "/torneos",
      icon: Trophy,
      visible: isOrganizer
    },
    {
      title: "Jugadores",
      url: "/jugadores",
      icon: Users,
      visible: isOrganizer
    },
    {
      title: "Ranking",
      url: "/ranking",
      icon: BarChart3,
      visible: true
    },
    {
      title: "Mi Perfil",
      url: "/perfil",
      icon: Users,
      visible: !isOrganizer
    },
    {
      title: "Mis Torneos",
      url: "/mis-torneos",
      icon: Trophy,
      visible: !isOrganizer
    },
    {
      title: "Mis Ligas",
      url: "/mis-ligas",
      icon: Target,
      visible: !isOrganizer
    },
    // Acceso a administración por cada circuito que el usuario organiza
    ...organizedCircuits.map((c) => ({
      title: `Administrar ${c.name}`,
      url: `/circuitos/${c.id}/admin`,
      icon: Award,
      visible: true
    }))
  ];

  const getInitials = (firstName?: string, lastName?: string) => {
    const first = firstName?.[0] || '';
    const last = lastName?.[0] || '';
    return (first + last).toUpperCase() || 'U';
  };

  return (
    <Sidebar data-testid="app-sidebar">
      <SidebarHeader className="p-4">
        <DropdownMenu>
          <DropdownMenuTrigger className="w-full" data-testid="user-menu-trigger">
            <div className="flex items-center gap-3 hover-elevate p-2 rounded-md">
              <Avatar>
                <AvatarImage src={user?.profileImageUrl || undefined} />
                <AvatarFallback className="bg-primary text-primary-foreground">
                  {getInitials(user?.firstName || undefined, user?.lastName || undefined)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 text-left">
                <p className="text-sm font-semibold text-sidebar-foreground">
                  {user?.fullName || user?.email || 'Usuario'}
                </p>
                <Badge variant="outline" className="text-xs">
                  {isOrganizer ? 'Organizador' : 'Jugador'}
                </Badge>
              </div>
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56">
            <DropdownMenuItem onClick={() => setLocation('/perfil')} data-testid="menu-perfil">
              <Users className="h-4 w-4 mr-2" />
              Mi Perfil
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setLocation('/settings')} data-testid="menu-settings">
              <Settings className="h-4 w-4 mr-2" />
              Configuración
            </DropdownMenuItem>
            <DropdownMenuItem onClick={async () => {
              console.log('🚨🚨🚨🚨🚨 SIDEBAR LOGOUT CALLED - VERSION 2026-01-30-00-08 🚨🚨🚨🚨🚨');
              
              // Get Supabase token
              let accessToken: string | null = null;
              
              // Debug: Check localStorage contents
              console.log('🔍 DEBUG - localStorage keys:', Object.keys(localStorage));
              console.log('🔍 DEBUG - supabase.auth.token:', localStorage.getItem('supabase.auth.token'));
              
              try {
                const sessionData = localStorage.getItem('supabase.auth.token');
                if (sessionData) {
                  const parsed = JSON.parse(sessionData);
                  accessToken = parsed?.access_token || null;
                  console.log('🔍 DEBUG - Parsed token:', !!accessToken);
                } else {
                  console.log('🔍 DEBUG - No supabase.auth.token found');
                }
              } catch (e) {
                console.log('Could not get token from localStorage:', e);
              }
              
              // Fallback: Try to get session from Supabase client
              if (!accessToken) {
                console.log('🔍 DEBUG - Trying Supabase client fallback...');
                try {
                  // Import and use Supabase client
                  const { supabase } = await import("@/lib/supabase/client");
                  const { data: { session } } = await supabase.auth.getSession();
                  accessToken = session?.access_token || null;
                  console.log('🔍 DEBUG - Supabase client token:', !!accessToken);
                } catch (e) {
                  console.log('🔍 DEBUG - Supabase client fallback failed:', e);
                }
              }
              
              console.log('🔐 Sidebar logout - Token available:', !!accessToken);
              
              try {
                const headers: Record<string, string> = {
                  'Content-Type': 'application/json',
                };
                
                if (accessToken) {
                  headers['Authorization'] = `Bearer ${accessToken}`;
                  console.log('✅ Token added to sidebar logout headers');
                } else {
                  console.log('❌ No token for sidebar logout - using credentials only');
                }
                
                const response = await fetch("/api/auth/logout", {
                  method: "POST",
                  headers,
                  credentials: "include",
                });
                
                console.log('📥 Sidebar logout response:', response.status);
                
                // Clear Supabase session
                if (accessToken) {
                  localStorage.removeItem('supabase.auth.token');
                  localStorage.removeItem('supabase.auth.refreshToken');
                  localStorage.removeItem('supabase.auth.user');
                }
                
                window.location.href = "/";
              } catch (error) {
                console.error("Error al cerrar sesión:", error);
                window.location.href = "/";
              }
            }} data-testid="menu-logout">
              <LogOut className="h-4 w-4 mr-2" />
              Cerrar Sesión
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Navegación</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {menuItems.filter(item => item.visible).map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton
                    asChild
                    isActive={location === item.url}
                    data-testid={`sidebar-${item.title.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <a href={item.url}>
                      <item.icon className="h-4 w-4" />
                      <span>{item.title}</span>
                    </a>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel>Herramientas</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild data-testid="sidebar-chat-ia">
                  <a href="#" onClick={(e) => { e.preventDefault(); /* TODO: Open chat widget */ }}>
                    <MessageCircle className="h-4 w-4" />
                    <span>Chat IA</span>
                  </a>
                </SidebarMenuButton>
              </SidebarMenuItem>
              {isOrganizer && (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild data-testid="sidebar-reportes">
                    <a href="/reportes">
                      <FileText className="h-4 w-4" />
                      <span>Reportes</span>
                    </a>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="p-4">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Trophy className="h-4 w-4 text-primary" />
          <span className="font-heading font-bold">Next Point</span>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
